<script setup lang="ts">
import { createPopper, type Instance as PopperInstance } from '@popperjs/core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { MrButton } from '#components';
import type { ButtonSize, ButtonType } from '#imports';

const {
    title = '',
    variant = 'filled',
    size = 'medium',
    disabled,
    onlyButton,
    minWidth = '116px',
} = defineProps<IProps>();
interface IProps {
    title?: string;
    variant?: ButtonType;
    size?: ButtonSize;
    disabled?: boolean;
    onlyButton?: boolean;
    minWidth?: string;
}
const root = ref<HTMLDivElement>();
const active = defineModel<boolean>();
onClickOutside(root, close);
const dropdown = ref<HTMLDivElement>();
const showInputButton = ref<InstanceType<typeof MrButton>>();
const popper = ref<PopperInstance>();

function onClick() {
    if (onlyButton) {
        active.value = !active.value;
        return;
    }
    if (active.value) {
        close();
    } else {
        active.value = true;
        if (dropdown && showInputButton && dropdown.value) {
            popper.value = createPopper(
                showInputButton.value?.$el,
                dropdown.value,
                {
                    placement: 'bottom-start',
                }
            );
        }
    }
}

onBeforeUnmount(() => popper.value?.destroy());

function close() {
    if (onlyButton) {
        return;
    }
    popper.value?.destroy();
    active.value = false;
}
</script>

<template>
    <div
        ref="root"
        class="mr-dropdown-container"
        :style="{ '--min-width': minWidth }"
        :class="{ active }"
    >
        <mr-button
            ref="showInputButton"
            class="dropdown-button"
            :variant
            :size
            :disabled
            :left-icon="faChevronDown"
            full-width
            @click.stop="onClick"
        >
            {{ title }}
        </mr-button>
        <div
            v-if="!onlyButton"
            v-show="active"
            ref="dropdown"
            class="content-container"
            @click="close"
        >
            <slot name="content" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.mr-dropdown-container {
    position: relative;
    &.active {
        .dropdown-button :deep(.icon) {
            transition: transform 300ms linear;
            transform: rotate(180deg);
        }
    }
}

.dropdown-button {
    min-width: var(--min-width);
    white-space: nowrap;
    &:deep(.icon) {
        transition: transform 300ms linear;
    }
}
.content-container {
    @include shadow(2);
    min-width: 200px;
    transition: opacity 500ms;
    padding: 5px;
    border-radius: 10px;
    background: white;
    z-index: 200;
}

@media (max-width: $app-mobile-max-width) {
    .content-container {
        min-width: 180px;
    }
}
</style>
